import { FC } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { PositioningAIGenerateFormData } from './positioning-ai-generate.schema';

interface PositioningAIGenerateFormProps {
  control: Control<PositioningAIGenerateFormData>;
  errors: FieldErrors<PositioningAIGenerateFormData>;
  isLoading: boolean;
}

const PositioningAIGenerateForm: FC<PositioningAIGenerateFormProps> = ({ control, errors, isLoading }) => {
  return (
    <>
      <div>
        <label htmlFor="company_description" className="block text-sm font-medium text-gray-700 mb-2">
          Company Description <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="company_description"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="company_description"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.company_description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Kami adalah platform SaaS yang membantu bisnis kecil mengelola operasi harian..."
              disabled={isLoading}
            />
          )}
        />
        {errors.company_description && (
          <p className="text-sm text-red-500 mt-1">{errors.company_description.message}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Berisi deskripsi singkat perusahaan. Hanya karakter alfanumerik + tanda baca umum yang diperbolehkan. Contoh:
          "Kami adalah platform SaaS yang membantu bisnis kecil mengelola operasi harian seperti penjadwalan, laporan,
          dan monitoring kinerja secara otomatis"
        </p>
      </div>

      <div>
        <label htmlFor="competitor" className="block text-sm font-medium text-gray-700 mb-2">
          Competitor <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="competitor"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="competitor"
              rows={2}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.competitor ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Google Workspace, Notion, ClickUp, Trello"
              disabled={isLoading}
            />
          )}
        />
        {errors.competitor && <p className="text-sm text-red-500 mt-1">{errors.competitor.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Boleh berisi beberapa nama kompetitor dipisah koma (misal: Google, Notion, ClickUp), tidak disarankan memakai
          simbol yang aneh ({'<>[]{},'} dll). Contoh: "Google Workspace, Notion, ClickUp, Trello"
        </p>
      </div>

      <div>
        <label htmlFor="unique_value" className="block text-sm font-medium text-gray-700 mb-2">
          Unique Value <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="unique_value"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="unique_value"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.unique_value ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Platform kami dioperasikan tanpa keahlian teknis..."
              disabled={isLoading}
            />
          )}
        />
        {errors.unique_value && <p className="text-sm text-red-500 mt-1">{errors.unique_value.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Menjelaskan value unik / keunggulan. Harus bisa dibaca sebagai kalimat/frasa normal. Contoh: "Platform kami
          dioperasikan tanpa keahlian teknis, mampu mengotomasi proses bisnis hanya dengan beberapa klik"
        </p>
      </div>

      <div>
        <label htmlFor="target_audience" className="block text-sm font-medium text-gray-700 mb-2">
          Target Audience <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="target_audience"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="target_audience"
              rows={2}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.target_audience ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="UMKM, pemilik usaha rintisan, freelancer..."
              disabled={isLoading}
            />
          )}
        />
        {errors.target_audience && <p className="text-sm text-red-500 mt-1">{errors.target_audience.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Menjelaskan target audience, tidak boleh cuma spasi. Contoh: "UMKM, pemilik usaha rintisan, freelancer, dan
          tim operasional kecil"
        </p>
      </div>
    </>
  );
};

export default PositioningAIGenerateForm;
